import { useState, useEffect, useRef, Fragment } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../../api';
import ActiveScenarioSessionTopBar, { ModeNotification } from './ActiveScenarioSessionTopBar';
import ActiveScenarioSessionInputBar from './ActiveScenarioSessionInputBar';
import CharacterSelectorBar from './CharacterSelectorBar';
import ScenarioMessage from './ScenarioMessage';
import SideDrawer from './SideDrawer';
import MobileScenarioInfoModal from './MobileScenarioInfoModal';
import ScrollToBottomButton from './ScrollToBottomButton';
import DevEventsPanel from './DevEventsPanel';
import DeathActions from './DeathActions';
import DeathMessage from './DeathMessage';
import styles from './ActiveScenarioSession.module.css';
import type { ScenarioType as Scenario } from '../../api/scenarios';
import type { ScenarioCharacter, ScenarioMessage as ScenarioMessageType, ScenarioDetailResponse } from '../../api/types/scenario';
import { useTimelineEvents, usePersonaDeathHandler, useCharacterDeathHandler } from './hooks/useTimelineEvents';
import { useDeathEvents } from './hooks/useDeathEvents';

// Define notification type
interface Notification {
  message: string;
  type: 'success' | 'error' | 'info';
}

function ActiveScenarioSession(): JSX.Element {
  const { scenarioId } = useParams<{ scenarioId: string }>();
  const navigate = useNavigate();
  const [scenario, setScenario] = useState<Scenario | null>(null);
  const [message, setMessage] = useState<string>('');
  const [selectedCharacter, setSelectedCharacter] = useState<ScenarioCharacter | null>(null);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isWaitingForResponse, setIsWaitingForResponse] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [hideCharacterSelector, setHideCharacterSelector] = useState<boolean>(false);
  const [isWaitingForAutoResponse, setIsWaitingForAutoResponse] = useState<boolean>(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);
  const [isMobileModalOpen, setIsMobileModalOpen] = useState<boolean>(false);
  const [isMobile, setIsMobile] = useState<boolean>(false);
  const [regeneratingMessageId, setRegeneratingMessageId] = useState<string | null>(null);
  const [editingMessageId, setEditingMessageId] = useState<string | null>(null);
  const [isRewinding, setIsRewinding] = useState<boolean>(false);
  const [rewindTargetId, setRewindTargetId] = useState<string | null>(null);
  const [isCreatingNewScenario, setIsCreatingNewScenario] = useState<boolean>(false);
  const [isInitialLoad, setIsInitialLoad] = useState<boolean>(true);
  const [notification, setNotification] = useState<Notification | null>(null);
  const [showBookGlow, setShowBookGlow] = useState<boolean>(false);
  const [glowType, setGlowType] = useState<'success' | 'error' | 'info'>('info');
  const [lastMessageAnimating, setLastMessageAnimating] = useState<boolean>(false);
  const [isNewMessageArriving, setIsNewMessageArriving] = useState<boolean>(false);
  const [isAutoMode, setIsAutoMode] = useState<boolean>(true); // Default to auto mode
  const [showModeNotification, setShowModeNotification] = useState<boolean>(false);
  const modeNotificationTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const messagesContainerRef = useRef<HTMLDivElement | null>(null);
  const { MessageDeathEvents, MessageLeaveEvents } = useTimelineEvents();
  
  // Use our new persona death handler hook
  const { 
    hasPersonaDied,
    deathMessageId,
    personaDeathData,
    resetPersonaDeathState
  } = usePersonaDeathHandler(scenario?.messages || []);
  
  // Use our new character death handler hook
  const {
    hasCharacterDied,
    characterDeathData,
    resetCharacterDeathState
  } = useCharacterDeathHandler(scenario?.messages || []);

  // Check if the device is mobile
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    // Initial check
    checkIfMobile();
    
    // Add event listener for window resize
    window.addEventListener('resize', checkIfMobile);
    
    // Cleanup
    return () => {
      window.removeEventListener('resize', checkIfMobile);
    };
  }, []);

  // Disable global scrolling while in the scenario session
  useEffect(() => {
    // Store original body style
    const originalOverflow = document.body.style.overflow;
    const originalPosition = document.body.style.position;
    const originalHeight = document.body.style.height;
    const originalWidth = document.body.style.width;
    
    // Disable scrolling on body
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.height = '100%';
    document.body.style.width = '100%';
    
    // Restore original style when component unmounts
    return () => {
      document.body.style.overflow = originalOverflow;
      document.body.style.position = originalPosition;
      document.body.style.height = originalHeight;
      document.body.style.width = originalWidth;
    };
  }, []);

  useEffect(() => {
    if (scenarioId) {
      loadScenario();
    }
  }, [scenarioId]);

  // Effect for initial page positioning
  useEffect(() => {
    // Only reset to top if there's no scenario loaded yet
    // Once scenario loads, the other effect will handle proper positioning
    if (!scenario) {
      window.scrollTo(0, 0);
      
      if (messagesContainerRef.current) {
        messagesContainerRef.current.scrollTop = 0;
      }
    }
  }, []); // Empty dependency array means this runs once on mount

  // Handle initial load animation and scrolling for new messages during conversation
  useEffect(() => {
    if (!scenario?.messages) return;

    // Check if there are any user-sent messages in the scenario
    const hasUserMessages = scenario.messages.some(msg => msg.member_type === 'persona');
    
    // Scenario with user messages should scroll to bottom on initial load
    if (isInitialLoad && hasUserMessages) {
      // Delay slightly to allow the UI to render first
      setTimeout(() => {
        scrollToBottom('auto');
        setIsInitialLoad(false);
      }, 100);
    }
    // For standard scrolling during conversation
    else if (!isInitialLoad && (isSubmitting || isWaitingForResponse)) {
      scrollToBottom();
    }
    // For initial load of scenarios with no user messages (brand new scenarios)
    else if (isInitialLoad && scenario.messages.length > 0) {
      // Just set isInitialLoad to false after a delay without scrolling
      // This allows messages to animate in without forcing scroll
      const lastMessageIndex = scenario.messages.length - 1;
      const totalDelay = 300 + (lastMessageIndex * 400) + 200;
      
      const timer = setTimeout(() => {
        setIsInitialLoad(false);
      }, totalDelay);
      
      return () => clearTimeout(timer);
    }
  }, [scenario?.messages, isSubmitting, isWaitingForResponse, isInitialLoad]);

  // Reset isWaitingForResponse when new messages are added to the scenario
  useEffect(() => {
    if (scenario?.messages && scenario.messages.length > 0) {
      setIsWaitingForResponse(false);
      
      // When a new message is added and it's not initial load,
      // set lastMessageAnimating to true
      if (!isInitialLoad) {
        setLastMessageAnimating(true);
        // Set isNewMessageArriving to true when a new message is added
        setIsNewMessageArriving(true);
        
        // Reset isNewMessageArriving after a delay
        const timer = setTimeout(() => {
          setIsNewMessageArriving(false);
        }, 1000); // Adjust this delay as needed
        
        return () => clearTimeout(timer);
      }
    }
  }, [scenario?.messages, isInitialLoad]);

  const loadScenario = async (): Promise<void> => {
    if (!scenarioId) return;
    
    try {
      // Initially set scroll position to top (will be adjusted based on content)
      window.scrollTo(0, 0);
      if (messagesContainerRef.current) {
        messagesContainerRef.current.scrollTop = 0;
      }
      
      const scenarioInstance = await api.scenarios.get(scenarioId);
      
      // Check if there are any user-sent messages
      // We'll use this to determine if we should scroll to bottom
      const hasUserMessages = scenarioInstance.messages.some(msg => msg.member_type === 'persona');
      
      // Set the scenario instance in state
      setScenario(scenarioInstance);
      
      // Load available characters
      await scenarioInstance.getAvailableCharacters();
      
      // Keep isInitialLoad true - the useEffect will handle:
      // 1. For new scenarios, start at top and animate messages
      // 2. For scenarios with user messages, scroll to bottom
    } catch (error) {
      console.error('Failed to load scenario:', error);
      setError('Failed to load scenario. Please try again.');
    }
  };

  const scrollToBottom = (behavior: ScrollBehavior = 'smooth'): void => {
    // For scenarios with user messages during initial load, use a more immediate scroll
    const isUserScenario = scenario?.messages?.some(msg => msg.member_type === 'persona');
    const effectiveBehavior = (isInitialLoad && isUserScenario) ? 'auto' : behavior;
    
    messagesEndRef.current?.scrollIntoView({ behavior: effectiveBehavior });
  };

  // Handle when the last message animation completes
  const handleLastMessageAnimationComplete = (): void => {
    setLastMessageAnimating(false);
    // No scrolling here - we've removed the automatic scroll behavior
    // on animation complete as requested by the user
  };

  const handleSend = async (): Promise<void> => {
    if (!message.trim() || isSubmitting || !scenario) return;

    setIsSubmitting(true);
    setIsInitialLoad(false);
    // Set isNewMessageArriving to true when user sends a message
    setIsNewMessageArriving(true);
    
    try {
      // Always scroll to bottom when sending a new message during conversation
      // This is not initial load, so scrolling here is appropriate
      scrollToBottom('auto');
      
      const updatedScenario = await scenario.addMessage(message.trim());
      setScenario(updatedScenario);
      setMessage('');
      setIsWaitingForResponse(true);
      // Set lastMessageAnimating to false since this is a user message
      setLastMessageAnimating(false);
      
      // Remove immediate timeline events processing - we'll handle this in auto mode
      // after the next speaker responds
      
      // In auto mode, automatically get and trigger the next speaker
      if (isAutoMode) {
        try {
          // Set waiting for auto response state to true to animate the character selector
          setIsWaitingForAutoResponse(true);
          
          // Get the next speaker using the latest scenario state
          const scenarioWithNextSpeaker = await updatedScenario.getNextSpeaker();
          
          if (scenarioWithNextSpeaker.next_speaker) {
            const { member_type, character } = scenarioWithNextSpeaker.next_speaker;
            
            // Short delay before triggering the next speaker's response
            // This gives a more natural conversation flow
            setTimeout(() => {
              if (member_type === 'narrator') {
                // Narrator speaking (null character)
                handleCharacterSelect(null);
              } else if (member_type === 'character' && character) {
                // Find the character object by ID
                const characterObj = scenarioWithNextSpeaker.characters.find(c => c.id === character.id);
                if (characterObj) {
                  handleCharacterSelect(characterObj);
                }
              }
            }, 500); // 500ms delay for natural feel
          } else {
            // If no next speaker, reset the auto response waiting state
            setIsWaitingForAutoResponse(false);
            
            // Remove timeline processing for user messages when there's no auto-response
          }
        } catch (error) {
          console.error('Failed to get next speaker:', error);
          // If getting next speaker fails, reset the auto response waiting state
          setIsWaitingForAutoResponse(false);
          
          // Remove timeline processing for user messages when next speaker fails
        }
      } else {
        // Remove timeline processing for user messages in manual mode
      }
    } catch (error) {
      console.error('Failed to send message:', error);
      setIsWaitingForResponse(false);
      // Reset auto response waiting state on error
      setIsWaitingForAutoResponse(false);
    } finally {
      setIsSubmitting(false);
      // Reset isNewMessageArriving after a delay
      setTimeout(() => {
        setIsNewMessageArriving(false);
      }, 1000); // Adjust this delay as needed
    }
  };

  const handleCharacterSelect = async (character: ScenarioCharacter | null): Promise<void> => {
    setSelectedCharacter(character);
    if (!isSubmitting && scenario) {
      setIsSubmitting(true);
      setIsWaitingForResponse(true);
      setHideCharacterSelector(true);
      // Reset the auto response waiting state when a character is manually selected
      setIsWaitingForAutoResponse(false);
      // Set isNewMessageArriving to true when a character is selected
      setIsNewMessageArriving(true);
      
      try {
        // This is part of active conversation, so scroll to bottom is appropriate here
        scrollToBottom('auto');
        
        const memberType = character ? 'character' : 'narrator';
        const characterId = character?.id;
        
        const responseScenario = await scenario.generate(memberType, characterId);
        setScenario(responseScenario);
        
        setSelectedCharacter(null);
      } catch (error) {
        console.error('Failed to generate response:', error);
        setSelectedCharacter(null);
      } finally {
        setIsSubmitting(false);
        setIsWaitingForResponse(false);
        setHideCharacterSelector(false);
        // Make sure to reset the auto response waiting state
        setIsWaitingForAutoResponse(false);
        // Reset isNewMessageArriving after a delay
        setTimeout(() => {
          setIsNewMessageArriving(false);
        }, 1000); // Adjust this delay as needed
      }
    }
  };

  const handleRegenerateMessage = async (messageId: string): Promise<void> => {
    if (!scenario) return;
    
    setRegeneratingMessageId(messageId);
    setIsInitialLoad(false);
    setIsNewMessageArriving(true);
    
    try {
      // This is an active user interaction, so scroll to bottom is appropriate
      scrollToBottom('auto');
      
      const updatedScenario = await scenario.regenerate(messageId);
      setScenario(updatedScenario);
    } catch (error) {
      console.error('Failed to regenerate message:', error);
    } finally {
      setRegeneratingMessageId(null);
      // Reset isNewMessageArriving after a delay
      setTimeout(() => {
        setIsNewMessageArriving(false);
      }, 1000);
    }
  };

  const handleRewindToMessage = async (messageId: string): Promise<void> => {
    if (!scenario) return;
    
    setIsRewinding(true);
    setRewindTargetId(messageId);
    setIsInitialLoad(false);
    setIsNewMessageArriving(true);
    
    try {
      // Add a small delay to allow the animation to play
      await new Promise(resolve => setTimeout(resolve, 800));
      
      // Explicitly scroll to the rewind target message
      // For rewind, we might want to scroll to the specific message
      // rather than the bottom
      const targetElement = document.getElementById(`message-${messageId}`);
      if (targetElement) {
        targetElement.scrollIntoView({ behavior: 'smooth' });
      }
      
      const updatedScenario = await scenario.rewindToMessage(messageId);
      setScenario(updatedScenario);
    } catch (error) {
      console.error('Failed to rewind to message:', error);
    } finally {
      setIsRewinding(false);
      setRewindTargetId(null);
      // Reset isNewMessageArriving after a delay
      setTimeout(() => {
        setIsNewMessageArriving(false);
      }, 1000);
    }
  };

  const handleEditMessage = async (messageId: string, content: string): Promise<void> => {
    if (!scenario) return;
    
    setEditingMessageId(messageId);
    setIsInitialLoad(false);
    setIsNewMessageArriving(true);
    
    try {
      // This is an active user interaction, so scroll to bottom is appropriate
      scrollToBottom('auto');
      
      const updatedScenario = await scenario.editMessage(messageId, content);
      setScenario(updatedScenario);
    } catch (error) {
      console.error('Failed to edit message:', error);
    } finally {
      setEditingMessageId(null);
      // Reset isNewMessageArriving after a delay
      setTimeout(() => {
        setIsNewMessageArriving(false);
      }, 1000);
    }
  };

  const handleNewScenarioFromMessage = async (messageId: string): Promise<void> => {
    if (!scenario || isSubmitting) return;
    
    setIsSubmitting(true);
    setIsCreatingNewScenario(true);
    
    try {
      // Log the characters from the current scenario before creating a new one
      console.log('Current scenario characters before creating new scenario:', scenario.characters);
      console.log('Current scenario definition:', scenario.definition);
      console.log('Number of messages in original scenario:', scenario.messages.length);
      
      // Find the index of the selected message
      const messageIndex = scenario.messages.findIndex(msg => msg.id === messageId);
      if (messageIndex !== -1) {
        console.log('Selected message index:', messageIndex);
        console.log('Messages up to selected message:', messageIndex + 1);
      }
      
      const newScenario = await scenario.newScenarioFromMessage(messageId);
      
      // Log the characters from the new scenario
      console.log('New scenario characters after creation:', newScenario.characters);
      console.log('New scenario definition:', newScenario.definition);
      console.log('Number of messages in new scenario:', newScenario.messages.length);
      
      // Navigate to the new scenario
      navigate(`/sessions/${newScenario.id}`);
    } catch (error) {
      console.error('Failed to create new scenario from message:', error);
    } finally {
      setIsSubmitting(false);
      setIsCreatingNewScenario(false);
    }
  };

  const handleBookClick = (): void => {
    if (isMobile) {
      setIsMobileModalOpen(true);
    } else {
      setIsDrawerOpen(true);
    }
  };

  const handleAddAvailableCharacter = async (character: any): Promise<void> => {
    if (!scenario) return;
    
    try {
      setIsSubmitting(true);
      await scenario.addAvailableCharacter(character.id);
      
      // Refresh the scenario to get the updated available characters
      await loadScenario();
    } catch (error) {
      console.error('Failed to add available character:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRemoveAvailableCharacter = async (characterId: string): Promise<void> => {
    if (!scenario) return;
    
    try {
      setIsSubmitting(true);
      await scenario.removeAvailableCharacter(characterId);
      
      // Refresh the scenario to get the updated available characters
      await loadScenario();
    } catch (error) {
      console.error('Failed to remove available character:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRemoveCharacter = async (character: ScenarioCharacter): Promise<void> => {
    if (!scenario) return;
    
    try {
      setIsSubmitting(true);
      
      // First remove the character from active characters
      await scenario.removeCharacter(character.id);
      
      // Then add it to available characters
      await scenario.addAvailableCharacter(character.id);
      
      // Refresh the scenario to get the updated characters
      await loadScenario();
      
      // If the removed character was selected, reset selection
      if (selectedCharacter?.id === character.id) {
        setSelectedCharacter(null);
      }
    } catch (error) {
      console.error('Failed to remove character:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Add a random character from the available characters list
  const handleAddRandomCharacter = async (): Promise<void> => {
    if (!scenario || !scenario.available_characters || scenario.available_characters.length === 0) {
      // If no available characters, open the drawer to let the user add some
      handleBookClick();
      // Show notification that there are no available characters
      setNotification({
        message: 'No available characters. Please add some from the character library.',
        type: 'info'
      });
      setTimeout(() => setNotification(null), 3000);
      
      return;
    }
    
    try {
      // Immediately hide character selector and show success glow
      setHideCharacterSelector(true);
      setGlowType('success');
      setShowBookGlow(true);
      
      setIsSubmitting(true);
      
      // This is an active user interaction, so scroll to bottom is appropriate
      scrollToBottom('auto');
      
      // Select a random character from the available characters
      const randomIndex = Math.floor(Math.random() * scenario.available_characters.length);
      const randomCharacter = scenario.available_characters[randomIndex];
      
      // Store character info before API calls
      const characterName = randomCharacter.display_name;
      const addedCharacterId = randomCharacter.id;
      
      // Add the character to the scenario
      const updatedScenario = await scenario.addCharacter(randomCharacter.id);
      
      // Remove the character from available characters
      await updatedScenario.removeAvailableCharacter(randomCharacter.id);
      
      // Update the local state with the updated scenario
      setScenario(updatedScenario);
      
      // Make sure the available characters are up to date
      await updatedScenario.getAvailableCharacters();
      setScenario(updatedScenario);
      
      // Show success notification
      setNotification({
        message: `${characterName} added to the conversation!`,
        type: 'success'
      });
      setTimeout(() => setNotification(null), 3000);
      
      // Keep glow effect for 3 seconds
      setTimeout(() => setShowBookGlow(false), 3000);
      
      // Find the character in the updated scenario
      const addedCharacter = updatedScenario.characters.find(char => char.id === addedCharacterId);
      
      console.log(`[DEBUG] Added character ID: ${addedCharacterId}`);
      console.log(`[DEBUG] Characters after adding:`, updatedScenario.characters);
      
      // If we found the character, automatically select them and make them talk
      if (addedCharacter) {
        console.log(`[DEBUG] Found character after adding:`, addedCharacter.display_name);
        // Use a longer delay to ensure UI updates properly
        setTimeout(() => {
          console.log(`[DEBUG] Making character speak:`, addedCharacter.display_name);
          handleCharacterSelect(addedCharacter);
        }, 600); // Increased from 300ms to 600ms
      } else {
        console.log(`[DEBUG] Could not find added character with ID ${addedCharacterId}`);
        // If we didn't find the character or aren't selecting one, show the character selector again
        setHideCharacterSelector(false);
      }
      
    } catch (error) {
      console.error('Failed to add random character:', error);
      
      // Revert UI changes if there's an error
      setHideCharacterSelector(false);
      
      // Show error notification
      setNotification({
        message: 'Failed to add character. Please try again.',
        type: 'error'
      });
      setTimeout(() => setNotification(null), 3000);
      
      // Change glow effect to error type
      setGlowType('error');
      setShowBookGlow(true);
      setTimeout(() => setShowBookGlow(false), 3000);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle mode change from top bar
  const handleModeChange = (newAutoMode: boolean): void => {
    setIsAutoMode(newAutoMode);
    
    // Clear any existing timeout
    if (modeNotificationTimeoutRef.current) {
      clearTimeout(modeNotificationTimeoutRef.current);
    }
    
    // Show the mode notification
    setShowModeNotification(true);
    
    // Hide the notification after 3 seconds
    modeNotificationTimeoutRef.current = setTimeout(() => {
      setShowModeNotification(false);
      modeNotificationTimeoutRef.current = null;
    }, 3000);
  };

  // Clean up timeout on unmount
  useEffect(() => {
    return () => {
      if (modeNotificationTimeoutRef.current) {
        clearTimeout(modeNotificationTimeoutRef.current);
      }
    };
  }, []);

  // Use our death events hook
  const { handlePlayAgain, handleRewindDeath } = useDeathEvents({
    scenario,
    deathMessageId,
    navigate,
    resetPersonaDeathState,
    handleRewindToMessage,
    setIsSubmitting,
    setIsInitialLoad,
    setNotification
  });

  // Debug logging for persona/character death state
  useEffect(() => {
    if (hasPersonaDied) {
      console.log('[DEBUG] Persona death detected in ActiveScenarioSession');
      console.log('[DEBUG] Death message ID:', deathMessageId);
      console.log('[DEBUG] Persona death data:', personaDeathData);
    }
    
    if (hasCharacterDied && characterDeathData) {
      console.log('[DEBUG] Character death detected in ActiveScenarioSession');
      console.log('[DEBUG] Character death data:', characterDeathData);
    }
  }, [hasPersonaDied, deathMessageId, personaDeathData, hasCharacterDied, characterDeathData]);

  // Refresh available characters when drawer opens
  useEffect(() => {
    const refreshAvailableCharacters = async () => {
      if (isDrawerOpen && scenario && scenarioId) {
        try {
          // Call getAvailableCharacters on the existing scenario
          await scenario.getAvailableCharacters();
          
          // Since getAvailableCharacters is mutable, we need to trigger a re-render
          // by creating a new reference while preserving all methods
          setScenario(prevScenario => {
            if (prevScenario) {
              return Object.assign(
                Object.create(Object.getPrototypeOf(prevScenario)),
                prevScenario
              );
            }
            return prevScenario;
          });
        } catch (error) {
          console.error('Failed to refresh available characters:', error);
        }
      }
    };

    refreshAvailableCharacters();
  }, [isDrawerOpen, scenario, scenarioId]);

  if (error) {
    return <div className={styles.error}>{error}</div>;
  }

  if (!scenario) {
    return <div className={styles.loading}>Loading...</div>;
  }

  // Create a placeholder loading message when waiting for a response
  const createLoadingMessage = (): ScenarioMessageType => {
    // We only create loading messages for character or narrator responses, never for persona
    const memberType = selectedCharacter ? 'character' : 'narrator';
    
    return {
      id: 'loading',
      content: '',
      member_type: memberType,
      character: selectedCharacter || undefined,
      persona: undefined,
      is_hidden: false, // Add this missing property to fix the linter error
      timeline_events: [] // Add empty timeline events array
    };
  };

  // Scroll to bottom button should be shown only on mobile
  const renderScrollToBottomButton = () => {
    if (!isMobile) return null;
    
    return (
      <ScrollToBottomButton 
        containerRef={messagesContainerRef}
        threshold={100}
        isNewMessageArriving={isNewMessageArriving || isWaitingForResponse}
      />
    );
  };

  return (
    <div className={styles.container}>
      <ActiveScenarioSessionTopBar 
        displayName={scenario?.definition?.display_name || 'Scenario'} 
        scenarioIcon={scenario?.definition?.icon}
        scenarioSessionId={scenarioId}
        scenarioDefinitionId={scenario?.definition?.id}
        onBookClick={handleBookClick}
        onDrawerToggle={() => setIsDrawerOpen(!isDrawerOpen)}
        isMobile={isMobile}
        onInfoClick={() => setIsMobileModalOpen(true)}
        onModeChange={handleModeChange}
        initialMode={isAutoMode ? 'auto' : 'manual'}
        renderNotificationInTopBar={false}
      />
      
      {/* Notification */}
      {notification && (
        <div className={`${styles.notification} ${styles[notification.type]}`}>
          {notification.message}
        </div>
      )}
      
      <div 
        className={`
          ${styles.messagesContainer} 
          ${isInitialLoad && !scenario.messages.some(msg => msg.member_type === 'persona') 
            ? styles.initialLoadContainer 
            : ''}
        `} 
        ref={messagesContainerRef}
        style={{ scrollBehavior: isInitialLoad ? 'auto' : 'smooth' }}
      >
        {scenario?.messages && scenario.messages.map((msg, index) => {
          // Move console.log statements outside of JSX rendering
          console.log(`[DEBUG] Rendering message ID: ${msg.id}, index: ${index}`);
          
          // Check for death events in this message
          const hasDeathEvents = msg.timeline_events?.some(event => 
            event.event_type === 'death' && 
            ((event.character && event.character.id) || 
            (event.member_type === 'persona' && event.persona))
          );
          
          if (hasDeathEvents) {
            console.log(`[DEBUG] Message ${msg.id} has death events`);
            console.log('[DEBUG] Message timeline events:', msg.timeline_events);
          }
          
          if (msg.timeline_events?.some(event => event.event_type === 'death')) {
            console.log(`[DEBUG] Rendering MessageDeathEvents for message ${msg.id}, skipPersonaDeaths=${msg.id === deathMessageId}, skipCharacterDeaths=${characterDeathData?.messageId === msg.id}`);
          }
          
          return (
            <Fragment key={`container-${msg.id}`}>
              <ScenarioMessage 
                key={`message-${msg.id}`} 
                message={msg}
                isLatestMessage={index === scenario.messages.length - 1}
                onRegenerate={handleRegenerateMessage}
                isRegenerating={regeneratingMessageId === msg.id}
                onEditMessage={handleEditMessage}
                onRewindToMessage={handleRewindToMessage}
                onNewScenarioFromMessage={handleNewScenarioFromMessage}
                isRewinding={isRewinding && rewindTargetId === msg.id}
                index={index}
                isInitialLoad={isInitialLoad}
                onAnimationComplete={
                  index === scenario.messages.length - 1 && !isInitialLoad
                    ? handleLastMessageAnimationComplete
                    : undefined
                }
              />
              {/* Render death events for this specific message, but skip both persona and character deaths that will be rendered outside the loop */}
              <MessageDeathEvents 
                message={msg} 
                skipPersonaDeaths={msg.id === deathMessageId}
                skipCharacterDeaths={characterDeathData?.messageId === msg.id}
              />
              {/* Render leave events for this specific message */}
              <MessageLeaveEvents message={msg} />
            </Fragment>
          );
        })}
        
        {/* Log before rendering the persona death message */}
        {hasPersonaDied && personaDeathData && (() => {
          console.log('[DEBUG] Rendering separate persona death message');
          return (
            <DeathMessage
              key={`persona-death-${personaDeathData.id}`}
              icon={personaDeathData.icon}
              displayName="You"
              isPersona={true}
            />
          );
        })()}
        
        {/* Render character death message if needed */}
        {hasCharacterDied && characterDeathData && (() => {
          console.log('[DEBUG] Rendering separate character death message');
          return (
            <DeathMessage
              key={`character-death-${characterDeathData.id}`}
              icon={characterDeathData.icon}
              displayName={characterDeathData.display_name}
              isPersona={false}
            />
          );
        })()}
        
        {/* Dev-only test controls for death events */}
        {scenario?.messages && import.meta.env.VITE_ENV_TIER === 'dev' && (
          <DevEventsPanel messages={scenario.messages} />
        )}
        
        {isWaitingForResponse && (
          <ScenarioMessage 
            message={createLoadingMessage()} 
            isLoading={true}
            isInitialLoad={false}
          />
        )}
        
        <div ref={messagesEndRef} />
      </div>

      {/* Scroll to bottom button */}
      {renderScrollToBottomButton()}

      <div className={styles.bottomControls}>
        {/* Mode notification above CharacterSelectorBar */}
        <div className={styles.modeNotificationContainer}>
          <ModeNotification
            isAutoMode={isAutoMode}
            show={showModeNotification}
            position="above-character-bar"
          />
        </div>
      
        {/* Only show the character selector bar if the persona hasn't died */}
        {!hasPersonaDied && (
          <CharacterSelectorBar
            characters={scenario.characters}
            selectedCharacter={selectedCharacter}
            onSelectCharacter={handleCharacterSelect}
            onRemoveCharacter={handleRemoveCharacter}
            onAddRandomCharacter={handleAddRandomCharacter}
            isLoading={hideCharacterSelector}
            isAutoMode={isAutoMode}
            isWaitingForAutoResponse={isWaitingForAutoResponse}
          />
        )}

        {/* Show death actions if input bar is hidden due to persona death */}
        {hasPersonaDied ? (
          <DeathActions 
            onPlayAgain={handlePlayAgain}
            onRewindDeath={handleRewindDeath}
          />
        ) : (
          <ActiveScenarioSessionInputBar
            message={message}
            setMessage={setMessage}
            handleSend={handleSend}
            isSubmitting={isSubmitting}
            onBookClick={handleBookClick}
            showBookGlow={showBookGlow}
            glowType={glowType}
          />
        )}
      </div>

      {/* Show SideDrawer on desktop */}
      {!isMobile && (
        <SideDrawer 
          isOpen={isDrawerOpen} 
          onClose={() => setIsDrawerOpen(false)}
          characters={scenario?.characters || []}
          availableCharacters={scenario?.available_characters || []}
          onAddAvailableCharacter={handleAddAvailableCharacter}
          onRemoveAvailableCharacter={handleRemoveAvailableCharacter}
          scenarioId={scenarioId}
        />
      )}

      {/* Show MobileScenarioInfoModal on mobile */}
      {isMobile && (
        <MobileScenarioInfoModal
          isOpen={isMobileModalOpen}
          onClose={() => setIsMobileModalOpen(false)}
          characters={scenario?.characters || []}
          availableCharacters={scenario?.available_characters || []}
          onAddAvailableCharacter={handleAddAvailableCharacter}
          onRemoveAvailableCharacter={handleRemoveAvailableCharacter}
          scenarioId={scenarioId}
        />
      )}
    </div>
  );
}

export default ActiveScenarioSession; 